import React, { useState, useRef, useEffect } from "react";
import { useAppStore } from "@/core/store/useAppStore";
import { Collection } from "@/services/collectionsService";
import { CollectionCard } from "./CollectionCard";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { MediaShareModal } from "@/domains/LibraryView/components/MediaShareModal";
import { UploadMediaModal } from "@/domains/LibraryView/components/UploadMediaModal";
import { EntityCreateButton } from "@/core/entityConfig";

interface CollectionsSidebarProps {
  clientId: string;
  onClose: () => void;
}

export const CollectionsSidebar: React.FC<CollectionsSidebarProps> = ({
  clientId,
  onClose,
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [isEditingId, setIsEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [deleteConfirmCollection, setDeleteConfirmCollection] = useState<Collection | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [shareCollectionId, setShareCollectionId] = useState<string | null>(null);
  const [uploadCollectionId, setUploadCollectionId] = useState<string | null>(null);
  const createInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  const {
    collections: collectionsState,
    setActiveCollection,
    createCollection,
    updateCollection,
    deleteCollection,
    fetchCollections,
  } = useAppStore();

  const { collections, activeCollectionId, isLoading } = collectionsState;

  // Fetch collections when component mounts
  useEffect(() => {
    if (clientId && collectionsState.lastFetchedClientId !== clientId) {
      fetchCollections(clientId);
    }
  }, [clientId, collectionsState.lastFetchedClientId, fetchCollections]);

  // Focus create input when creating
  useEffect(() => {
    if (isCreating && createInputRef.current) {
      createInputRef.current.focus();
    }
  }, [isCreating]);

  // Focus edit input when editing
  useEffect(() => {
    if (isEditingId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [isEditingId]);

  const handleCreate = async () => {
    if (!newCollectionName.trim()) return;

    try {
      const collection = await createCollection(clientId, {
        name: newCollectionName.trim(),
      });
      setNewCollectionName("");
      setIsCreating(false);
      setActiveCollection(collection.id);
    } catch (err) {
      console.error("Failed to create collection:", err);
    }
  };

  const handleCreateKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCreate();
    } else if (e.key === "Escape") {
      setIsCreating(false);
      setNewCollectionName("");
    }
  };

  const handleStartEdit = (collection: Collection) => {
    setIsEditingId(collection.id);
    setEditName(collection.name || "");
  };

  const handleSaveEdit = async () => {
    if (!isEditingId || !editName.trim()) return;

    try {
      await updateCollection(clientId, isEditingId, {
        name: editName.trim(),
      });
      setIsEditingId(null);
      setEditName("");
    } catch (err) {
      console.error("Failed to update collection:", err);
    }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === "Escape") {
      setIsEditingId(null);
      setEditName("");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmCollection) return;

    setIsDeleting(true);
    try {
      await deleteCollection(clientId, deleteConfirmCollection.id);
      setDeleteConfirmCollection(null);
    } catch (err) {
      console.error("Failed to delete collection:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="w-80 shrink-0 border-l border-gray-200 bg-gray-50 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
        <h2 className="text-lg font-semibold text-gray-900">Collections</h2>
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600 rounded-sm transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Create new collection */}
      <div className="p-3 border-b border-gray-200 bg-white">
        {isCreating ? (
          <div className="flex gap-2">
            <input
              ref={createInputRef}
              type="text"
              value={newCollectionName}
              onChange={(e) => setNewCollectionName(e.target.value)}
              onKeyDown={handleCreateKeyDown}
              placeholder="Collection name..."
              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-hidden focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
            <button
              onClick={handleCreate}
              disabled={!newCollectionName.trim()}
              className="px-3 py-2 text-sm bg-cyan-500 text-white rounded-md hover:bg-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Add
            </button>
            <button
              onClick={() => {
                setIsCreating(false);
                setNewCollectionName("");
              }}
              className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        ) : (
          <EntityCreateButton
            entity="collection"
            onClick={() => setIsCreating(true)}
            variant="secondary"
            className="w-full"
          />
        )}
      </div>

      {/* Collection list */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-cyan-500 rounded-full animate-spin" />
          </div>
        ) : collections.length === 0 ? (
          <div className="text-center py-8">
            <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <p className="text-sm text-gray-500">No collections yet</p>
            <p className="text-xs text-gray-400 mt-1">Create one to organize your media</p>
          </div>
        ) : (
          <>
            {/* "All items" option to clear selection */}
            <div
              onClick={() => setActiveCollection(null)}
              className={`
                p-3 rounded-lg cursor-pointer transition-all duration-200
                ${!activeCollectionId
                  ? "bg-gray-100 border-2 border-gray-300"
                  : "bg-white border border-gray-200 hover:border-gray-300 hover:shadow-xs"
                }
              `}
            >
              <div className="flex items-center gap-3">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                <span className={`text-sm font-medium ${!activeCollectionId ? "text-gray-900" : "text-gray-600"}`}>
                  All Items
                </span>
              </div>
            </div>

            {/* Collection cards */}
            {collections.map((collection) => (
              isEditingId === collection.id ? (
                <div key={collection.id} className="p-3 bg-white border border-cyan-300 rounded-lg">
                  <div className="flex gap-2">
                    <input
                      ref={editInputRef}
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={handleEditKeyDown}
                      className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded-sm focus:outline-hidden focus:ring-2 focus:ring-cyan-500"
                    />
                    <button
                      onClick={handleSaveEdit}
                      disabled={!editName.trim()}
                      className="px-2 py-1 text-sm bg-cyan-500 text-white rounded-sm hover:bg-cyan-600 disabled:opacity-50"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingId(null);
                        setEditName("");
                      }}
                      className="px-2 py-1 text-sm text-gray-500 hover:text-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <CollectionCard
                  key={collection.id}
                  collection={collection}
                  isSelected={activeCollectionId === collection.id}
                  onSelect={() => setActiveCollection(collection.id)}
                  onEdit={() => handleStartEdit(collection)}
                  onDelete={() => setDeleteConfirmCollection(collection)}
                  onShare={() => setShareCollectionId(collection.id)}
                  onUpload={() => setUploadCollectionId(collection.id)}
                />
              )
            ))}
          </>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        isOpen={!!deleteConfirmCollection}
        onClose={() => setDeleteConfirmCollection(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Collection"
        message={`Are you sure you want to delete "${deleteConfirmCollection?.name || 'this collection'}"? The media items will not be deleted, only removed from this collection.`}
        confirmText="Delete"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Share modal */}
      <MediaShareModal
        isOpen={!!shareCollectionId}
        onClose={() => setShareCollectionId(null)}
        collectionId={shareCollectionId || undefined}
        clientId={clientId}
      />

      {/* Upload to collection modal */}
      <UploadMediaModal
        isOpen={!!uploadCollectionId}
        onClose={() => setUploadCollectionId(null)}
        clientId={clientId}
        contentType="auto"
        collectionId={uploadCollectionId ?? undefined}
        onUploadComplete={() => {
          setUploadCollectionId(null);
          fetchCollections(clientId);
        }}
      />
    </div>
  );
};
